/**
 * FP · Track your order (/pages/track-your-order). Renders easyship's view of
 * an order into sections/fp-track.liquid.
 *
 * API: POST <data-api> with { order, phone } (the form) or { token } (the link
 * in the shipping email, ?t=<32 hex>). See fp-easyship docs/public-tracking-api.md.
 *   200 view · 404 not_found (wrong number OR wrong phone, deliberately the same)
 *   400 bad_request · 429 slow_down (Retry-After: 60)
 *
 * Rules from the API:
 * - The phone goes in the request body only, never in a URL.
 * - Nothing is stored in localStorage / sessionStorage (the view has the address).
 * - Times are UTC ISO strings, shown in IST. Money is integer paise.
 * - Customer-typed text (name, address) is written with textContent only.
 *
 * Written for older in-app browsers too: no optional chaining, no `??`.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-fp-track]');
  if (!root || root.__fpTrack) return;
  root.__fpTrack = true;

  var API = root.getAttribute('data-api') || 'https://easyship.fabricpandit.com/api/public/track';
  var WA = root.getAttribute('data-wa') || '912616770775';
  var WA_TEXT = root.getAttribute('data-wa-text') || 'Hi, I need help with my order.';
  var OFD_TIME = root.getAttribute('data-ofd-time') || 'by 7 PM';

  function $(sel, scope) {
    return (scope || root).querySelector(sel);
  }

  var form = $('[data-form]');
  var orderInput = form.elements.order;
  var phoneInput = form.elements.phone;
  var errorEl = $('[data-error]');
  var noticeEl = $('[data-notice]');
  var submitBtn = $('[data-submit]');
  var submitLabel = $('[data-submit-label]');
  var submitText = submitLabel.textContent;
  var announceEl = $('[data-announce]');
  var helpLink = $('[data-help]');
  var helpHref = helpLink ? helpLink.getAttribute('href') : '';
  var copyBtn = $('[data-copy]');
  var againBtn = $('[data-again]');

  /* ------------------------------------------------------------ analytics */

  function track(name, params) {
    try {
      if (typeof window.fpTrack === 'function') window.fpTrack(name, params || {});
    } catch (e) {
      /* analytics never breaks the page */
    }
  }

  /* ---------------------------------------------------------- formatting */

  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var IST_OFFSET_MS = 330 * 60 * 1000; // India has no daylight saving

  function ist(date) {
    var t = new Date(date.getTime() + IST_OFFSET_MS);
    return { y: t.getUTCFullYear(), m: t.getUTCMonth(), d: t.getUTCDate(), wd: t.getUTCDay(), h: t.getUTCHours(), min: t.getUTCMinutes() };
  }

  function dayKey(p) {
    return p.y * 10000 + (p.m + 1) * 100 + p.d;
  }

  function parseIso(s) {
    if (!s) return null;
    var d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  /** 'YYYY-MM-DD' (an IST calendar date) → that day at noon IST. */
  function parseDay(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 6, 30)) : null;
  }

  function todayKey(offsetDays) {
    return dayKey(ist(new Date(Date.now() + (offsetDays || 0) * 86400000)));
  }

  /** "Sat, 22 Aug" */
  function shortDate(d) {
    var p = ist(d);
    return WEEKDAYS[p.wd].slice(0, 3) + ', ' + p.d + ' ' + MONTHS[p.m].slice(0, 3);
  }

  /** "27 Aug" */
  function dayMonth(d) {
    var p = ist(d);
    return p.d + ' ' + MONTHS[p.m].slice(0, 3);
  }

  /** "7:05 PM" */
  function clock(d) {
    var p = ist(d);
    var h = p.h % 12 || 12;
    return h + ':' + (p.min < 10 ? '0' : '') + p.min + ' ' + (p.h < 12 ? 'AM' : 'PM');
  }

  /** Hero lines: ["Thursday,", "27 August"], or Today / Tomorrow / Yesterday. */
  function heroDate(d) {
    var p = ist(d);
    var k = dayKey(p);
    var first = WEEKDAYS[p.wd] + ',';
    if (k === todayKey(0)) first = 'Today,';
    else if (k === todayKey(1)) first = 'Tomorrow,';
    else if (k === todayKey(-1)) first = 'Yesterday,';
    return [first, p.d + ' ' + MONTHS[p.m]];
  }

  /** Integer paise → "₹2,597" (Indian digit grouping; paise only when there are any). */
  function rupees(paise) {
    var n = Math.round(Number(paise) || 0);
    var whole = String(Math.floor(Math.abs(n) / 100));
    var frac = Math.abs(n) % 100;
    if (whole.length > 3) {
      whole = whole.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + whole.slice(-3);
    }
    return (n < 0 ? '−' : '') + '₹' + whole + (frac ? '.' + (frac < 10 ? '0' : '') + frac : '');
  }

  /** "BHIWANDI_HUB" → "Bhiwandi Hub"; short vowel-less codes stay capitals ("DELHI NCR" → "Delhi NCR"). */
  function titleCase(s) {
    return String(s || '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map(function (word) {
        if (/^[A-Z]{2,3}$/.test(word) && !/[AEIOU]/.test(word)) return word;
        return word.toLowerCase().replace(/(^|[(\-\/,.])([a-z])/g, function (m, a, b) {
          return a + b.toUpperCase();
        });
      })
      .join(' ');
  }

  /** The place for the status line: "BHIWANDI HUB" → "Bhiwandi" (as the design writes it). */
  var FACILITY = /\s+(hub|gateway|gw|dc|dpc|ndc|branch|facility|centre|center|sortation|sort|warehouse|terminal|office|station|depot|ho|bo|so|ph|pc|apex|surface)$/i;
  function placeName(loc) {
    var full = titleCase(loc);
    var s = full;
    var prev;
    do {
      prev = s;
      s = s.replace(/\s*\([^)]*\)\s*$/, '').replace(FACILITY, '');
    } while (s && s !== prev);
    return s || full;
  }

  function sentence(s) {
    var t = String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
  }

  function plural(n, one, many) {
    return n + ' ' + (n === 1 ? one : many);
  }

  function safeUrl(url) {
    return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : '';
  }

  /** Shopify CDN images are resized server-side with ?width=. */
  function sizedImage(url, width) {
    var safe = safeUrl(url);
    if (!safe) return '';
    try {
      var u = new URL(safe);
      if (/cdn\.shopify\.com$|\/cdn\/shop\//i.test(u.hostname + u.pathname)) u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return safe;
    }
  }

  /* ------------------------------------------------------------- the copy */

  var STEP_LABEL = {
    placed: 'Order placed',
    dispatched: 'Dispatched from Surat',
    in_transit: 'In transit',
    out_for_delivery: 'Out for delivery',
    delivered: 'Delivered',
  };

  var STOPPED = { returning: true, returned: true, cancelled: true };

  /** Friendly words for a courier scan; the courier's own text is the fallback. */
  var SCAN_COPY = {
    CREATED: 'Packed and ready for dispatch',
    PICKUP_SCHEDULED: 'Courier pickup booked',
    PICKED_UP: 'Shipment picked up',
    OUT_FOR_DELIVERY: 'Out for delivery',
    DELIVERED: 'Delivered',
    RTO_INITIATED: 'Return to Fabric Pandit started',
    RTO_IN_TRANSIT: 'On its way back to us',
    RTO_DELIVERED: 'Returned to Fabric Pandit',
    CANCELLED: 'Shipment cancelled',
  };

  function scanText(scan) {
    var raw = String(scan.text || '');
    if (scan.status === 'IN_TRANSIT') {
      if (/ARRIV|RECEIV|IN-?SCAN|REACH/i.test(raw)) return 'Arrived at the hub';
      if (/OUT-?SCAN|DEPART|CONNECT|FORWARD|LEFT|BAGG/i.test(raw)) return 'Left the hub';
      return 'In transit';
    }
    if (scan.status === 'NDR') return raw ? 'Delivery attempted: ' + sentence(raw).replace(/^ndr\s*[-:]?\s*/i, '') : 'Delivery attempted';
    return SCAN_COPY[scan.status] || sentence(raw) || 'Update from the courier';
  }

  function courierName(shipment) {
    if (!shipment) return '';
    return [shipment.courier, shipment.service].filter(function (x) {
      return x && String(x).trim();
    }).join(' ');
  }

  /** Status line, eyebrow and the two hero lines for each of easyship's eight stages. */
  function heroFor(view) {
    var st = view.status || {};
    var exp = parseDay(st.expectedDelivery);
    var expAhead = exp && dayKey(ist(exp)) >= todayKey(0);
    var where = st.location ? placeName(st.location) : '';
    function at(label) {
      return where ? label + ' · ' + where : label;
    }
    switch (st.stage) {
      case 'processing':
        return { tone: 'live', status: 'Being packed', eyebrow: expAhead ? 'Arriving' : 'Order confirmed', lines: expAhead ? heroDate(exp) : ['Packing', 'your order'] };
      case 'out_for_delivery':
        return { tone: 'live', status: at('Out for delivery'), eyebrow: 'Arriving', lines: ['Today,', OFD_TIME] };
      case 'attempted':
        return { tone: 'live', status: at('Delivery attempted'), eyebrow: expAhead ? 'Next attempt' : 'Delivery attempted', lines: expAhead ? heroDate(exp) : ['We will', 'try again'] };
      case 'delivered': {
        var delivered = parseIso(st.deliveredAt) || parseIso(st.updatedAt);
        return { tone: 'done', status: 'Delivered', eyebrow: 'Delivered on', lines: delivered ? heroDate(delivered) : ['Delivered', ''] };
      }
      case 'returning':
        return { tone: 'stopped', status: at('Returning to us'), eyebrow: 'Delivery unsuccessful', lines: ['On its way', 'back to us'] };
      case 'returned': {
        var back = parseIso(st.updatedAt);
        return { tone: 'stopped', status: 'Returned to us', eyebrow: back ? 'Returned on' : 'Returned', lines: back ? heroDate(back) : ['Back with us', ''] };
      }
      case 'cancelled':
        return { tone: 'stopped', status: 'Cancelled', eyebrow: 'Order', lines: ['Cancelled', ''] };
      case 'in_transit':
      default:
        return { tone: 'live', status: at('In transit'), eyebrow: expAhead ? 'Arriving' : 'In transit', lines: expAhead ? heroDate(exp) : ['On its way', ''] };
    }
  }

  function stepCopy(step, view, index) {
    var stage = view.status.stage;
    var order = view.order;
    var shipment = view.shipment;
    var atDate = parseIso(step.at);
    var exp = parseDay(view.status.expectedDelivery);
    var label = STEP_LABEL[step.key] || step.key;
    var date = '';
    var detail = '';

    if (step.state === 'done') {
      if (atDate) date = step.key === 'delivered' ? dayMonth(atDate) + ', ' + clock(atDate) : shortDate(atDate);
    } else if (step.state === 'current') {
      date = stage === 'processing' ? '' : 'Now';
    } else if ((step.key === 'out_for_delivery' || step.key === 'delivered') && exp && !STOPPED[stage]) {
      date = 'Est ' + dayMonth(exp);
    }

    switch (step.key) {
      case 'placed':
        if (step.state === 'done') {
          detail = plural(order.itemCount || (order.items || []).length, 'item', 'items') + ', ' + (order.paymentMode === 'cod' ? 'cash on delivery.' : 'paid online.');
        }
        break;
      case 'dispatched':
        if (step.state === 'current') {
          label = 'Being packed in Surat';
          detail = shipment && shipment.courier ? 'Packed. Waiting for ' + shipment.courier + ' to pick it up.' : 'We are packing your order in Surat.';
        } else if (step.state === 'done' && shipment) {
          detail = 'Handed to ' + courierName(shipment) + (atDate ? ', ' + clock(atDate) : '') + '.';
        }
        break;
      case 'in_transit': {
        if (step.state === 'current') {
          var latest = parseIso(view.status.updatedAt);
          detail = view.status.location
            ? 'Latest scan at ' + titleCase(view.status.location) + (latest ? ', ' + dayMonth(latest) + ', ' + clock(latest) : '') + '.'
            : 'On its way to you.';
        } else if (step.state === 'done') {
          var lastHub = null;
          (view.scans || []).some(function (s) {
            if (s.status === 'IN_TRANSIT' && s.location) {
              lastHub = s.location;
              return true;
            }
            return false;
          });
          if (lastHub) detail = 'Last scan at ' + titleCase(lastHub) + '.';
        }
        break;
      }
      case 'out_for_delivery':
        if (step.state === 'current') {
          detail = stage === 'attempted'
            ? 'A delivery attempt did not go through. The courier will try again.'
            : 'With the rider today. Keep your phone close.';
        } else if (step.state === 'upcoming' && !STOPPED[stage]) {
          detail = 'You will get an SMS the morning it is with the rider.';
        }
        break;
      case 'delivered':
        if (step.state === 'done') detail = 'Received at the door. Thank you for shopping with us.';
        else if (!STOPPED[stage]) detail = 'We will confirm here once it reaches you.';
        break;
    }
    return { label: label, date: date, detail: detail };
  }

  function itemMeta(item) {
    var variant = item.variant && item.variant !== 'Default Title' ? String(item.variant) : '';
    var qty = Number(item.qty) || 1;
    if (/^by met(er|re)$/i.test(variant)) {
      return qty + 'm · ' + rupees(item.unitPricePaise) + ' /mtr';
    }
    var parts = [];
    if (variant) parts.push(variant);
    if (qty > 1) parts.push('Qty ' + qty);
    return parts.join(' · ');
  }

  function totalLabel(view) {
    if (view.order.paymentMode !== 'cod') return 'Total paid';
    if (view.status.stage === 'delivered') return 'Total paid';
    if (STOPPED[view.status.stage]) return 'Order total';
    return 'To pay on delivery';
  }

  /* -------------------------------------------------------------- render */

  function clone(name) {
    var tpl = $('template[data-tpl="' + name + '"]');
    return tpl.content.firstElementChild.cloneNode(true);
  }

  function setText(el, text) {
    if (el) el.textContent = text == null ? '' : String(text);
  }

  function show(el, on) {
    if (el) el.hidden = !on;
  }

  function normalise(view) {
    var keys = ['placed', 'dispatched', 'in_transit', 'out_for_delivery', 'delivered'];
    var steps = Array.isArray(view.steps) && view.steps.length ? view.steps : keys.map(function (k) {
      return { key: k, state: 'upcoming', at: null };
    });
    view.steps = steps;
    view.scans = Array.isArray(view.scans) ? view.scans : [];
    view.order.items = Array.isArray(view.order.items) ? view.order.items : [];
    view.order.number = String(view.order.number || '').replace(/^#/, '');
    return view;
  }

  function render(view) {
    view = normalise(view);
    var order = view.order;
    var shipment = view.shipment;
    var hero = heroFor(view);

    // Status line + hero
    var status = $('[data-view="result"] .fp-track__status');
    var pulse = $('[data-pulse]');
    status.classList.toggle('is-done', hero.tone === 'done');
    status.classList.toggle('is-stopped', hero.tone === 'stopped');
    pulse.classList.toggle('is-live', hero.tone === 'live');
    pulse.classList.toggle('is-done', hero.tone === 'done');
    pulse.classList.toggle('is-stopped', hero.tone === 'stopped');
    setText($('[data-status-label]'), hero.status);
    setText($('[data-eyebrow]'), hero.eyebrow);
    setText($('[data-hero-a]'), hero.lines[0]);
    setText($('[data-hero-b]'), hero.lines[1] || '');
    setText(
      $('[data-order-line]'),
      'Order #' + order.number + ' · ' + plural(order.itemCount || order.items.length, 'item', 'items') + ' · ' + rupees(order.totalPaise)
    );

    // "Track on <courier>": only when the courier has its own page
    var ctaLink = $('[data-courier-link]');
    var trackingUrl = shipment ? safeUrl(shipment.trackingUrl) : '';
    show($('[data-ctas]'), Boolean(trackingUrl));
    if (trackingUrl) {
      ctaLink.setAttribute('href', trackingUrl);
      ctaLink.setAttribute('data-courier', shipment.courier || '');
      setText($('[data-courier-cta-label]'), 'Track on ' + shipment.courier);
    }

    // Timeline
    var list = $('[data-steps]');
    list.textContent = '';
    var steps = view.steps;
    var stage = view.status.stage;
    var current = -1;
    steps.forEach(function (s, i) {
      if (s.state === 'current' && current === -1) current = i;
    });
    var logIndex = stage === 'delivered' ? steps.length - 1 : current;
    if (logIndex < 0) {
      for (var i = steps.length - 1; i >= 0; i--) {
        if (steps[i].state === 'done') {
          logIndex = i;
          break;
        }
      }
    }

    steps.forEach(function (step, index) {
      var li = clone('step');
      var copy = stepCopy(step, view, index);
      li.classList.add('is-' + (step.state === 'done' || step.state === 'current' ? step.state : 'upcoming'));
      var next = steps[index + 1];
      if (step.state === 'done' && next && next.state === 'done') li.classList.add('is-linked');
      setText($('[data-label]', li), copy.label);
      setText($('[data-date]', li), copy.date);
      setText($('[data-detail]', li), copy.detail);

      if (index === logIndex && view.scans.length) {
        var scansEl = $('[data-scans]', li);
        var toggle = $('[data-toggle]', li);
        var toggleLabel = $('[data-toggle-label]', li);
        var scansId = 'FpTrackScans-' + Date.now();
        scansEl.id = scansId;
        toggle.setAttribute('aria-controls', scansId);
        fillScans(scansEl, view);
        toggle.hidden = false;
        toggle.addEventListener('click', function () {
          var open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          setText(toggleLabel, open ? 'Hide scan history' : 'See scan history');
          scansEl.hidden = !open;
          if (open) track('fp_track_scans', { fp_stage: stage });
        });
      }
      list.appendChild(li);
    });

    // Courier
    var courierBlock = $('[data-courier-block]');
    show(courierBlock, Boolean(shipment && courierName(shipment)));
    if (shipment) {
      setText($('[data-courier-name]'), courierName(shipment));
      show($('[data-awb-row]'), Boolean(shipment.awb));
      setText($('[data-awb]'), shipment.awb ? 'AWB ' + shipment.awb : '');
      copyBtn.setAttribute('data-awb-value', shipment.awb || '');
      setText(copyBtn, 'Copy');
    }

    // Delivering to
    var to = order.deliverTo || {};
    var place = [to.city, to.pincode].filter(function (x) {
      return x && String(x).trim();
    }).join(' ');
    var addressLines = (Array.isArray(to.lines) ? to.lines : []).concat(place ? [place] : []);
    show($('[data-address-block]'), Boolean(to.name || addressLines.length));
    setText($('[data-ship-name]'), to.name || '');
    setText($('[data-ship-address]'), addressLines.join('\n'));

    // In this order
    var itemsEl = $('[data-items]');
    itemsEl.textContent = '';
    order.items.forEach(function (item) {
      var row = clone('item');
      var img = $('[data-img]', row);
      var src = sizedImage(item.imageUrl, 120);
      if (src) {
        img.src = src;
        img.hidden = false;
      }
      setText($('[data-title]', row), item.title);
      setText($('[data-meta]', row), itemMeta(item));
      setText($('[data-price]', row), rupees(item.totalPaise));
      itemsEl.appendChild(row);
    });
    setText($('[data-total-label]'), totalLabel(view));
    setText($('[data-total]'), rupees(order.totalPaise));

    // WhatsApp help carries the order number
    if (helpLink) {
      var msg = WA_TEXT.replace(/\.\s*$/, '') + ' #' + order.number + '.';
      helpLink.setAttribute('href', 'https://wa.me/' + WA + '?text=' + encodeURIComponent(msg));
    }

    return hero;
  }

  function fillScans(container, view) {
    container.textContent = '';
    var courier = view.shipment ? view.shipment.courier : '';
    var rows = view.scans.slice();
    var placed = parseIso(view.order.placedAt);
    if (placed) rows.push({ at: view.order.placedAt, status: 'PLACED', text: 'Order placed', location: null });
    rows.forEach(function (scan) {
      var d = parseIso(scan.at);
      var row = clone('scan');
      setText($('[data-when]', row), d ? shortDate(d) + ' · ' + clock(d) : '');
      var text = scan.status === 'PLACED' ? 'Order placed' : scanText(scan);
      var where = scan.status === 'PLACED' ? 'fabricpandit.com' : titleCase(scan.location);
      if (scan.status === 'PICKED_UP' && courier) where = where ? where + ' · ' + courier : courier;
      setText($('[data-text]', row), text);
      setText($('[data-place]', row), where);
      container.appendChild(row);
    });
  }

  /* -------------------------------------------------------------- states */

  function setState(state) {
    root.setAttribute('data-state', state);
    markFirstFacts();
  }

  /** On desktop the rail's first visible block drops its top rule (design: fact1). */
  function markFirstFacts() {
    var views = root.querySelectorAll('[data-rail-view]');
    Array.prototype.forEach.call(views, function (v) {
      var marked = false;
      Array.prototype.forEach.call(v.querySelectorAll('.fp-track__fact'), function (f) {
        var first = !marked && !f.hidden;
        f.classList.toggle('is-first', first);
        if (first) marked = true;
      });
    });
  }

  function announce(text) {
    setText(announceEl, '');
    window.setTimeout(function () {
      setText(announceEl, text);
    }, 50);
  }

  function showError(text) {
    setText(errorEl, text);
    show(errorEl, Boolean(text));
  }

  function showNotice(text) {
    setText(noticeEl, text);
    show(noticeEl, Boolean(text));
  }

  var MESSAGES = {
    not_found: 'We could not find an order with that number and phone number. Please check both and try again.',
    slow_down: 'Too many tries in a minute. Please wait a minute and try again.',
    bad_request: 'Please check the order number and phone number, then try again.',
    error: 'We could not reach our tracking service. Check your connection and try again.',
  };

  /* -------------------------------------------------------------- lookup */

  function post(body, timeoutMs) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? window.setTimeout(function () {
      controller.abort();
    }, timeoutMs) : null;
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
      cache: 'no-store',
      signal: controller ? controller.signal : undefined,
    }).then(
      function (res) {
        if (timer) window.clearTimeout(timer);
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            return { status: res.status, ok: res.ok, data: data };
          });
      },
      function () {
        if (timer) window.clearTimeout(timer);
        return { status: 0, ok: false, data: null };
      }
    );
  }

  var busy = false;

  function lookup(body, method) {
    if (busy) return;
    busy = true;
    showError('');
    if (method === 'form') {
      submitBtn.disabled = true;
      setText(submitLabel, 'Looking up…');
    } else {
      setState('loading');
    }

    post(body, 15000).then(function (res) {
      busy = false;
      submitBtn.disabled = false;
      setText(submitLabel, submitText);

      var view = res.data;
      if (res.ok && view && view.order && view.status) {
        var hero;
        try {
          hero = render(view);
        } catch (e) {
          hero = null;
        }
        if (hero) {
          showNotice('');
          setState('result');
          announce('Order #' + view.order.number + ': ' + hero.status + '.');
          track('fp_track_lookup', { fp_method: method, fp_result: 'found', fp_stage: view.status.stage });
          var h1 = $('[data-view="result"] .fp-track__h1');
          if (method === 'form' && h1) {
            h1.setAttribute('tabindex', '-1');
            h1.focus({ preventScroll: true });
          }
          var top = root.getBoundingClientRect().top;
          if (top < 0) root.scrollIntoView({ block: 'start' });
          return;
        }
      }

      var result = res.status === 404 ? 'not_found' : res.status === 429 ? 'slow_down' : res.status === 400 ? 'bad_request' : 'error';
      track('fp_track_lookup', { fp_method: method, fp_result: result });

      if (method === 'token') {
        setState('form');
        showNotice(
          result === 'not_found' || result === 'bad_request'
            ? 'This tracking link is not valid any more. Enter your order number and phone number instead.'
            : result === 'slow_down'
              ? MESSAGES.slow_down
              : 'We could not open your order just now. Enter your order number and phone number, or try the link again in a minute.'
        );
        return;
      }
      showError(MESSAGES[result]);
      if (result === 'not_found') orderInput.focus();
    });
  }

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    var order = orderInput.value.trim().replace(/^#/, '').replace(/\s+/g, '');
    var digits = phoneInput.value.replace(/\D/g, '');
    orderInput.removeAttribute('aria-invalid');
    phoneInput.removeAttribute('aria-invalid');
    if (!order) {
      orderInput.setAttribute('aria-invalid', 'true');
      showError('Enter your order number.');
      orderInput.focus();
      return;
    }
    if (digits.length < 10) {
      phoneInput.setAttribute('aria-invalid', 'true');
      showError('Enter the 10-digit phone number you gave at checkout.');
      phoneInput.focus();
      return;
    }
    // Only digits travel: easyship matches the last ten, and accepts 10–20 characters.
    lookup({ order: order.slice(0, 32), phone: digits.slice(-12) }, 'form');
  });

  againBtn.addEventListener('click', function () {
    if (/[?&]t=/.test(location.search) && window.history && history.replaceState) {
      var url = new URL(location.href);
      url.searchParams.delete('t');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }
    form.reset();
    showError('');
    showNotice('');
    if (helpLink) helpLink.setAttribute('href', helpHref);
    setState('form');
    orderInput.focus();
  });

  copyBtn.addEventListener('click', function () {
    var awb = copyBtn.getAttribute('data-awb-value') || '';
    if (!awb) return;
    copyText(awb).then(function () {
      setText(copyBtn, 'Copied');
      window.clearTimeout(copyBtn.__t);
      copyBtn.__t = window.setTimeout(function () {
        setText(copyBtn, 'Copy');
      }, 1800);
      track('fp_track_copy_awb', {});
    });
  });

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(function () {
        legacyCopy(text);
      });
    }
    legacyCopy(text);
    return Promise.resolve();
  }

  function legacyCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
    } catch (e) {
      /* nothing else to try */
    }
    document.body.removeChild(ta);
  }

  root.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    var courier = target.closest('[data-courier-link]');
    if (courier) track('fp_track_courier', { fp_courier: courier.getAttribute('data-courier') || '' });
    var pick = target.closest('[data-fp-track-pick]');
    if (pick) track('fp_track_pick', { fp_item: pick.getAttribute('data-fp-track-pick') || '' });
  });

  /* ---------------------------------------------------------------- boot */

  markFirstFacts();
  var tokenMatch = /[?&]t=([0-9a-f]{32})(?:&|$)/i.exec(location.search);
  if (tokenMatch) {
    lookup({ token: tokenMatch[1].toLowerCase() }, 'token');
  } else if (root.getAttribute('data-state') !== 'form') {
    setState('form');
  }
})();
